import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { CatalogView } from '../components/CatalogView';
import { useAuth } from '../context/AuthContext';
import {
  PawPrint,
  Plus,
  Shield,
  Heart,
  MapPin,
  X,
  AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const SPECIES_FILTERS = [
  { id: null,            label: 'All Animals' },
  { id: 'dogs',          label: 'Dogs' },
  { id: 'cats',          label: 'Cats' },
  { id: 'birds',         label: 'Birds' },
  { id: 'small_animals', label: 'Small Animals' },
  { id: 'fish_aquatic',  label: 'Fish & Aquatic' },
  { id: 'reptiles',      label: 'Reptiles' },
  { id: 'animals_other', label: 'Other' },
];

function PetsHero({ user, onListClick }) {
  return (
    <div className="px-4 md:px-6 pt-4">
      <div
        className="rounded-2xl p-6"
        style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)' }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <PawPrint className="w-6 h-6 text-[#F8F5F0]" />
              <h1 className="text-xl font-bold text-[#F8F5F0]">WANTD Pets</h1>
            </div>
            <p className="text-sm max-w-md" style={{ color: 'rgba(248,245,240,0.8)' }}>
              Rehome or find a pet — free for private rehomers, welfare-focused, local.
              Regulated listings only.
            </p>
            <div className="flex flex-wrap gap-3 mt-3 text-xs" style={{ color: 'rgba(248,245,240,0.6)' }}>
              <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Microchip verified</span>
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Local only — no shipping</span>
              <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> Free for private rehomers</span>
            </div>
          </div>
          {user && (
            <Button
              onClick={onListClick}
              className="bg-[#F8F5F0] text-[#1E3A5F] hover:bg-white font-bold flex items-center gap-2 flex-shrink-0 rounded-xl"
            >
              <Plus className="w-4 h-4" />
              List a Pet
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function PetsTermsBanner({ onDismiss }) {
  return (
    <div className="mx-3 md:mx-4 mt-3 flex items-start gap-3 p-4 rounded-lg border border-[var(--theme-border)] bg-[var(--theme-card)]">
      <AlertCircle className="w-5 h-5 text-[#D97706] mt-0.5 flex-shrink-0" />
      <p className="text-sm text-[var(--theme-text-secondary)] flex-1">
        WANTD Pets is for genuine rehoming and welfare-compliant sales. By using this section you agree to our{' '}
        <Link to="/pets/terms" className="underline font-medium text-[var(--theme-text-primary)]">Pets Terms & Conditions</Link>.
      </p>
      <button
        onClick={onDismiss}
        className="text-[var(--theme-text-muted)] hover:text-[var(--theme-text-primary)] flex-shrink-0"
        aria-label="Dismiss terms banner"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function PetsLandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [termsBannerDismissed, setTermsBannerDismissed] = useState(
    () => localStorage.getItem('pets_terms_seen') === 'true'
  );

  const dismissTermsBanner = () => {
    localStorage.setItem('pets_terms_seen', 'true');
    setTermsBannerDismissed(true);
  };

  return (
    <Layout>
      <div className={`w-full ${user ? '-mt-4 md:-mt-6' : '-mt-5 md:-mt-8'}`}>
        <CatalogView
          lockedCategory="live_animals"
          lockedSubcategoryOptions={SPECIES_FILTERS}
          linkPrefix="/pets"
          heroSlot={<PetsHero user={user} onListClick={() => navigate('/pets/list')} />}
          bannerSlot={!termsBannerDismissed && <PetsTermsBanner onDismiss={dismissTermsBanner} />}
          showCategoryDropdown={false}
          showViewToggle={false}
          sectionTitle="Pets Near You"
          itemNoun="pets"
          defaultSortBy="newest"
        />
      </div>
    </Layout>
  );
}
